import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Button, Checkbox, Input, message, Radio, Space, Steps } from 'antd';
import MaterialIcon from '../../../components/Icons/MaterialIcon';
import { theme } from '../../../styles/theme';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { RootState } from '../../../redux/store';
import { clientQuestionnairesActions } from '../../../redux/slices/clientQuestionnaireSlice';
import { debounce } from '../../../helpers';
import { useCreateQuestionnaireResponse } from '../../../hooks/client/useClientQuestionnaire';
import { useParams } from 'react-router-dom';
import { ClientQuestionnaireType } from '../../../types';

const MobileStyledSteps = styled(Steps)`
  padding: 0 1rem;
  .ant-steps-item {
    .ant-steps-item-icon {
      background-color: #1e9dbb; /* Active step color */
      color: #fff;
      // margin-inline-end: 2px !important;
    }

    &.ant-steps-item-finish .ant-steps-item-icon {
      background-color: #1e9dbb;
      color: #fff;
    }

    &.ant-steps-item-wait .ant-steps-item-icon {
      background-color: #cfeff4; /* Inactive step color */
      color: #1e9dbb; /* Number color */
    }

    /* Hide titles by default */
    .ant-steps-item-title {
      display: none;
    }

    /* Show title only for the active step */
    &.ant-steps-item-active .ant-steps-item-title {
      display: block;
    }
  }
`;

const DesktopStyledSteps = styled(Steps)`
  padding: 0 1rem;
  .ant-steps-item {
    .ant-steps-item-icon {
      background-color: #1e9dbb;
      color: #fff;
      margin-inline-end: 5px !important;
    }

    &.ant-steps-item-finish .ant-steps-item-icon {
      background-color: #cfeff4;
      color: #fff;
    }

    &.ant-steps-item-wait .ant-steps-item-icon {
      background-color: #cfeff4; /* Inactive step color */
      color: #1e9dbb; /* Number color */
    }

    .ant-steps-item-title {
      // display: none;
    }
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  padding: 0 1rem;

  @media (min-width: 768px) {
    justify-content: flex-end;
  }
`;

const MobileContainer = styled.section`
  width: inherit;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (min-width: 768px) {
    display: none;
  }
`;

const Content = styled.div`
  flex-grow: 2;
  flex: 2;
  overflow-y: auto;
  margin: 1rem 0;
  padding: 0 1rem;
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 70dvh;
  gap: .75rem;

  > h3 {
    font-weight: bold;
    font-size: 1.5rem;
  }

  @media (min-width: 768px) {
    display: none;
  }
`;

const QuestionsWrapper = styled.div`
  margin: 1.5rem 0;
  display: flex;
  flex-direction: column;
  gap: .5rem;
`;

const QuestionWrapper = styled.div`
  > label {
    padding: 0 1rem;
  }
`;

const QuestionGroupTitle = styled.h2`
  font-weight: 600;
  font-size: 32px;
  text-align: center;
  color: ${theme.color.primary};
  margin-bottom: 1rem;
`;

const OptionsWrapper = styled.div`
  background: #F7FAFC;
  padding: 1rem;

  > p {
    font-weight: bold;
    margin-bottom: 1.5rem;
  }
`;

const StyledRadioGroup = styled(Radio.Group)`
  width: 100%;

  > .ant-space {
    width: 100%;

    > .ant-space-item {
      width: 100%;

      .ant-radio-button-wrapper {
        width: 100%;

        &.ant-radio-button-wrapper-checked {
          background-color: #BADAE1;
          border-color: #2B8EA1;
          color: black;
          font-weight: bold;
        }

        &:hover {
          background-color: #BADAE1;
          border-color: #2B8EA1;
        }
      }
    }
  }
`;

const DesktopContainer = styled.section`
  display: flex;
  gap: 1.5rem;
  position: relative;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: .75rem;

  > h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    font-weight: inherit;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const StickyStepsContainer = styled.div`
  position: sticky;
  top: 60px;
  padding: 1rem .5rem 0 .5rem;
  width: max-content;
  background: white;
  border-radius: .25rem;
  box-shadow: 0 10px 15px -3px rgba(0,0,0,.1),0 4px 6px -2px rgba(0,0,0,.05)
`;

const PageWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 1.5rem 0;

  > span {
    padding: 0.25rem 0;
    border-bottom: 1px solid #2d3748;
  }
`;

const DesktopStepTitle = styled.h2`
  font-size: 1.25rem;
  text-align: center;
  margin-bottom: 1.5rem;
  font-weight: 400;
`;

const DesktopQuestionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  > label {
    text-decoration: underline;
    font-weight: bold;
  }
`;

const DesktopOptionsWrapper = styled.div`
  border: 1px solid #e2e8f0;
  margin-top: 1rem;
  background: white;
  border-radius: .25rem;
  padding: 1rem;

  > p {
    font-weight: bold;
    margin-bottom: 1rem;
  }
`;

const DesktopStyledRadioGroup = styled(Radio.Group)`
  width: 100%;
  display: flex;

  > .ant-space {
    flex: 1;

    > .ant-space-item {
      flex: 1;
      width: 100%;

      .ant-radio-button-wrapper {
        width: 100%;

        &.ant-radio-button-wrapper-checked {
          background-color: #BADAE1;
          border-color: #2B8EA1;
          color: #2B8EA1;
          font-weight: bold;
        }

        &:hover {
          background-color: #BADAE1;
          border-color: #2B8EA1;
        }
      }

      > label {
        width: 100%;
        text-align: center;
      }
    }
  }
`;

const Sublabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: .5rem;

  > p {
    font-weight: 400;
  }
`;

const TableStyleContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TableOptionsWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  border: 1px solid #e2e8f0;
  margin-top: 1rem;
  background: white;
  border-radius: .25rem;
  padding: 1rem;

  > p {
    width: 80%;
    font-weight: bold;
    margin-bottom: 1rem;
  }
`;

const DesktopStyledTableRadioGroup = styled(Radio.Group)`
  width: 100%;
  display: flex;

  > .ant-space {
    flex: 1;
    display: flex;

    > .ant-space-item {
      flex: 1;
      width: 100%;

      .ant-radio-button-wrapper {
        width: 100%;

        &.ant-radio-button-wrapper-checked {
          background-color: #BADAE1;
          border-color: #2B8EA1;
          color: #2B8EA1;
          font-weight: bold;
        }

        &:hover {
          background-color: #BADAE1;
          border-color: #2B8EA1;
        }
      }

      > label {
        width: 100%;
        text-align: center;
      }
    }
  }
`;

const debouncedOnChange = debounce((value: string, questionNum: number, type: string, index: number, onChangeSubquestionOfSubquestions: any) => {
  onChangeSubquestionOfSubquestions(value, questionNum, type, index);
}, 1000);

const Step1 = () => {
  const selectMyIntegratedHealth = createSelector(
    (state: RootState) => state.clientQuestionnaires.myIntegratedHealth,
    (myIntegratedHealth) => ({
      currentPage: myIntegratedHealth.currentPage,
      questions: myIntegratedHealth.questions,
      totalPage: myIntegratedHealth.totalPage,
    })
  );

  const { currentPage, questions, totalPage } = useSelector(selectMyIntegratedHealth);
  const dispatch = useDispatch();
  const onChangeRadio = (value: string, questionNumber: number) => {
    dispatch(clientQuestionnairesActions.toggleChoice({ value, questionNumber, type: 'myIntegratedHealth' }))
  };

  const onChangeSubquestion = useCallback((value: string, questionNumber: number, inputType: string) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestion({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType
      })
    );
  }, [dispatch]);

  const onChangeSubquestions = useCallback((value: string, questionNumber: number, inputType: string, subquestionsIndex: number) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestions({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType,
        subquestionsIndex
      })
    );
  }, [dispatch]);

  const onChangeSubquestionOfSubquestions = useCallback((value: string, questionNumber: number, inputType: string, subquestionsIndex: number) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestionOfSubquestions({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType,
        subquestionsIndex
      })
    );
  }, [dispatch]);

  return (
    <>
      <StepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <QuestionsWrapper>
          <QuestionGroupTitle>A. Attention</QuestionGroupTitle>
          <QuestionWrapper>
            <label>A1. Concentration</label>
            {questions
              .filter((q) => q.question_type === 'A1. Concentration')
              .map((question) => {
                const subquestion = question?.subquestion;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                return (
                  <OptionsWrapper key={`question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.title}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`question_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>

                    {subquestion && showSubquestion && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.title}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>A2. Numb</label>
            {questions
              .filter((q) => q.question_type === 'A2. Numb')
              .map((question) => (
                <OptionsWrapper key={`question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>A3. Confused</label>
            {questions
              .filter((q) => q.question_type === 'A3. Confused')
              .map((question) => (
                <OptionsWrapper key={`question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>A4. Reading and writing</label>
            {questions
              .filter((q) => q.question_type === 'A4. Reading and writing')
              .map((question) => {
                const subquestion = question?.subquestion;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                return (
                  <OptionsWrapper key={`question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`question_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>

                    {subquestion && showSubquestion && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>A5. Memory</label>
            {questions
              .filter((q) => q.question_type === 'A5. Memory')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                return (
                  <OptionsWrapper key={`question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`question_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && showSubquestion && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && showSubquestion && (
                      subquestions?.map((subq: any, index: number) => {
                        const showSubquestion = subq.showSubquestionValue === subq.choices.find((choice: any) => choice.selected)?.title;
                        return (
                          <>
                            {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                              <OptionsWrapper key={`subquestions_${question.question_num}_${index}`}>
                                <p>{subq.label}</p>
                                <StyledRadioGroup
                                  onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                  optionType="button"
                                >
                                  <Space direction="vertical">
                                    {subq.choices.map((choice: any, index: number) => (
                                      <Radio
                                        key={`subquestion_choice_${index}_${question.question_num}`}
                                        value={choice.title}
                                      >
                                        {choice.title}
                                      </Radio>
                                    ))}
                                  </Space>
                                </StyledRadioGroup>
                                {showSubquestion && (
                                  subq?.subquestion?.type === 'input' && (
                                    <OptionsWrapper key={question.question_num}>
                                      <p>{subq?.subquestion.label}</p>
                                      <Input
                                        defaultValue={subq?.subquestion.value}
                                        onChange={(e) => {
                                          debouncedOnChange(e.target.value, question.question_num, subq?.subquestion?.type, index, onChangeSubquestionOfSubquestions);
                                        }}
                                        placeholder={subq?.subquestion.placeholder ?? 'Write here...'}
                                      />
                                    </OptionsWrapper>
                                  )
                                )}
                              </OptionsWrapper>
                            )}
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`subquestions_${question.question_num}_${index}`}>
                                <p>{subq.label}</p>
                                <Input
                                  value={subq.value}
                                  onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
        </QuestionsWrapper>
      </StepContainer >
      <DesktopStepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
        </p>
        <p>
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <PageWrapper>
          <span>Page {currentPage} of {totalPage}</span>
        </PageWrapper>
        <DesktopStepTitle>
          A. Attention
        </DesktopStepTitle>
        <DesktopQuestionWrapper>
          <label>A1. Concentration</label>
          {questions
            .filter((q) => q.question_type === 'A1. Concentration')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
              return (
                <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="horizontal">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_desktop_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                  {subquestion && showSubquestion && (
                    <>
                      {subquestion.type === 'radio' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <DesktopStyledRadioGroup
                            onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                            value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                            optionType="button"
                          >
                            <Space direction={subquestion.type === 'radio' ? 'vertical' : 'horizontal'}>
                              {subquestion.choices.map((choice: any, index: number) => (
                                <Radio
                                  key={`subquestion_choice_desktop_${index}_${question.question_num}`}
                                  value={choice.title}
                                >
                                  {choice.title}
                                </Radio>
                              ))}
                            </Space>
                          </DesktopStyledRadioGroup>
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>A2. Numb</label>
          {questions
            .filter((q) => q.question_type === 'A2. Numb')
            .map((question) => (
              <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.[0]}
                  optionType="button"
                >
                  <Space direction="horizontal">
                    {question.choices.map((choice: any, index: number) => (
                      <Radio
                        key={`question_choice_desktop_${index}_${question.question_num}`}
                        value={choice.title}
                      >
                        {choice.title}
                      </Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                <Sublabels>
                  <p>{question.option_label_left}</p>
                  <p>{question.option_label_right}</p>
                </Sublabels>
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>A3. Confused</label>
          {questions
            .filter((q) => q.question_type === 'A3. Confused')
            .map((question) => (
              <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.[0]}
                  optionType="button"
                >
                  <Space direction="horizontal">
                    {question.choices.map((choice: any, index: number) => (
                      <Radio
                        key={`question_choice_desktop_${index}_${question.question_num}`}
                        value={choice.title}
                      >
                        {choice.title}
                      </Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                <Sublabels>
                  <p>{question.option_label_left}</p>
                  <p>{question.option_label_right}</p>
                </Sublabels>
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>A4. Reading and writing</label>
          {questions
            .filter((q) => q.question_type === 'A4. Reading and writing')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
              return (
                <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction="horizontal">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_desktop_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                  {subquestion && showSubquestion && (
                    <>
                      {subquestion.type === 'radio' && (
                        <DesktopOptionsWrapper key={`desktop_subquestion_${question.question_num}`}>
                          <p>{subquestion.label}</p>
                          <DesktopStyledRadioGroup
                            onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                            value={subquestion.choices.find((choice: any) => choice.selected)[0]}
                            optionType="button"
                          >
                            <Space direction={subquestion.type === 'radio' ? 'vertical' : 'horizontal'}>
                              {subquestion.choices.map((choice: any, index: number) => (
                                <Radio
                                  key={`subquestion_choice_desktop_${index}_${question.question_num}`}
                                  value={choice.title}
                                >
                                  {choice.title}
                                </Radio>
                              ))}
                            </Space>
                          </DesktopStyledRadioGroup>
                        </DesktopOptionsWrapper>
                      )}
                      {subquestion.type === 'input' && (
                        <DesktopOptionsWrapper key={`desktop_subquestion_${question.question_num}`}>
                          <p>{subquestion.label}</p>
                          <Input
                            defaultValue={subquestion.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                            placeholder={subquestion.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>A5. Memory</label>
          {questions
            .filter((q) => q.question_type === 'A5. Memory')
            .map((question) => {
              const subquestions = question?.subquestions;
              const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
              return (
                <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="horizontal">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_desktop_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                  {subquestions && showSubquestion && (
                    subquestions?.map((subq: any, index: number) => {
                      const showSubquestion = subq.showSubquestionValue === subq.choices.find((choice: any) => choice.selected)?.title;
                      return (
                        <>
                          {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                            <DesktopOptionsWrapper key={`desktop_subquestions_${question.question_num}`}>
                              <p>{subq.label}</p>
                              <DesktopStyledRadioGroup
                                onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                optionType="button"
                              >
                                <Space direction={subq.type === 'radio' ? 'vertical' : 'horizontal'}>
                                  {subq.choices.map((choice: any, index: number) => (
                                    <Radio
                                      key={`subquestions_choice_desktop_${index}_${question.question_num}`}
                                      value={choice.title}
                                    >
                                      {choice.title}
                                    </Radio>
                                  ))}
                                </Space>
                              </DesktopStyledRadioGroup>
                              {subq?.type === 'horizontal-radio' && (
                                <Sublabels>
                                  <p>{question.option_label_left}</p>
                                  <p>{question.option_label_right}</p>
                                </Sublabels>
                              )}
                              {showSubquestion && (
                                subq?.subquestion?.type === 'input' && (
                                  <DesktopOptionsWrapper key={`desktop_subquestions_${question.question_num}`}>
                                    <p>{subq?.subquestion.label}</p>
                                    <Input
                                      defaultValue={subq?.subquestion.value}
                                      onChange={(e) => {
                                        debouncedOnChange(e.target.value, question.question_num, subq?.subquestion?.type, index);
                                      }}
                                      placeholder={subq?.subquestion.placeholder ?? 'Write here...'}
                                    />
                                  </DesktopOptionsWrapper>
                                )
                              )}
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'input' && (
                            <DesktopOptionsWrapper key={`desktop_subquestions_${question.question_num}`}>
                              <p>{subq.label}</p>
                              <Input
                                value={subq.value}
                                onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                        </>
                      )
                    })
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
      </DesktopStepContainer>
    </>
  )
};

const Step2 = () => {
  const { currentPage, questions, totalPage } = useSelector((state: RootState) => state.clientQuestionnaires.myIntegratedHealth);
  const dispatch = useDispatch();
  const onChangeRadio = (value: string, questionNumber: number) => {
    dispatch(clientQuestionnairesActions.toggleChoice({ value, questionNumber, type: 'myIntegratedHealth' }))
  };

  return (
    <>
      <StepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <QuestionsWrapper>
          <QuestionGroupTitle>B. Mood</QuestionGroupTitle>
          <QuestionWrapper>
            <label>B1. Anger</label>
            {questions
              .filter((q) => q.question_type === 'B1. Anger')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>B2. Fear, Panic</label>
            {questions
              .filter((q) => q.question_type === 'B2. Fear, Panic')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>B3. Nervous, irritable</label>
            {questions
              .filter((q) => q.question_type === 'B3. Nervous, irritable')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>B4. Gloomy, depressed</label>
            {questions
              .filter((q) => q.question_type === 'B4. Gloomy, depressed')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>B5. Mood swings</label>
            {questions
              .filter((q) => q.question_type === 'B5. Mood swings')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>B6. Emotions</label>
            {questions
              .filter((q) => q.question_type === 'B6. Emotions')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>B7. Happy</label>
            {questions
              .filter((q) => q.question_type === 'B7. Happy')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>B8. Shy</label>
            {questions
              .filter((q) => q.question_type === 'B8. Shy')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
        </QuestionsWrapper>
      </StepContainer>
      <DesktopStepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
        </p>
        <p>
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <PageWrapper>
          <span>Page {currentPage} of {totalPage}</span>
        </PageWrapper>
        <DesktopStepTitle>
          B. Mood
        </DesktopStepTitle>
        <DesktopQuestionWrapper>
          <label>B1. Anger</label>
          {questions
            .filter((q) => q.question_type === 'B1. Anger')
            .map((question) => (
              <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction="horizontal">
                    {question.choices.map((choice: any, index: number) => (
                      <Radio
                        key={`question_choice_desktop_${index}_${question.question_num}`}
                        value={choice.title}
                      >
                        {choice.title}
                      </Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                <Sublabels>
                  <p>{question.option_label_left}</p>
                  <p>{question.option_label_right}</p>
                </Sublabels>
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>B2. Fear, Panic</label>
          {questions
            .filter((q) => q.question_type === 'B2. Fear, Panic')
            .map((question) => (
              <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction="horizontal">
                    {question.choices.map((choice: any, index: number) => (
                      <Radio
                        key={`question_choice_desktop_${index}_${question.question_num}`}
                        value={choice.title}
                      >
                        {choice.title}
                      </Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                <Sublabels>
                  <p>{question.option_label_left}</p>
                  <p>{question.option_label_right}</p>
                </Sublabels>
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>B3. Nervous, irritable</label>
          {questions
            .filter((q) => q.question_type === 'B3. Nervous, irritable')
            .map((question) => (
              <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio
                        key={`question_choice_desktop_${index}_${question.question_num}`}
                        value={choice.title}
                      >
                        {choice.title}
                      </Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>B4. Gloomy, depressed</label>
          {questions
            .filter((q) => q.question_type === 'B4. Gloomy, depressed')
            .map((question) => (
              <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction="horizontal">
                    {question.choices.map((choice: any, index: number) => (
                      <Radio
                        key={`question_choice_desktop_${index}_${question.question_num}`}
                        value={choice.title}
                      >
                        {choice.title}
                      </Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                <Sublabels>
                  <p>{question.option_label_left}</p>
                  <p>{question.option_label_right}</p>
                </Sublabels>
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>B5. Mood swings</label>
          {questions
            .filter((q) => q.question_type === 'B5. Mood swings')
            .map((question) => (
              <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction="horizontal">
                    {question.choices.map((choice: any, index: number) => (
                      <Radio
                        key={`question_choice_desktop_${index}_${question.question_num}`}
                        value={choice.title}
                      >
                        {choice.title}
                      </Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                <Sublabels>
                  <p>{question.option_label_left}</p>
                  <p>{question.option_label_right}</p>
                </Sublabels>
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>B6. Emotions</label>
          {questions
            .filter((q) => q.question_type === 'B6. Emotions')
            .map((question) => (
              <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction="horizontal">
                    {question.choices.map((choice: any, index: number) => (
                      <Radio
                        key={`question_choice_desktop_${index}_${question.question_num}`}
                        value={choice.title}
                      >
                        {choice.title}
                      </Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                <Sublabels>
                  <p>{question.option_label_left}</p>
                  <p>{question.option_label_right}</p>
                </Sublabels>
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>B7. Happy</label>
          {questions
            .filter((q) => q.question_type === 'B7. Happy')
            .map((question) => (
              <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction="horizontal">
                    {question.choices.map((choice: any, index: number) => (
                      <Radio
                        key={`question_choice_desktop_${index}_${question.question_num}`}
                        value={choice.title}
                      >
                        {choice.title}
                      </Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                <Sublabels>
                  <p>{question.option_label_left}</p>
                  <p>{question.option_label_right}</p>
                </Sublabels>
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>B8. Shy</label>
          {questions
            .filter((q) => q.question_type === 'B8. Shy')
            .map((question) => (
              <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction="horizontal">
                    {question.choices.map((choice: any, index: number) => (
                      <Radio
                        key={`question_choice_desktop_${index}_${question.question_num}`}
                        value={choice.title}
                      >
                        {choice.title}
                      </Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                <Sublabels>
                  <p>{question.option_label_left}</p>
                  <p>{question.option_label_right}</p>
                </Sublabels>
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
      </DesktopStepContainer>
    </>
  )
};

const Step3 = () => {
  const { currentPage, questions, totalPage } = useSelector((state: RootState) => state.clientQuestionnaires.myIntegratedHealth);
  const dispatch = useDispatch();
  const onChangeRadio = (value: string, questionNumber: number) => {
    dispatch(clientQuestionnairesActions.toggleChoice({ value, questionNumber, type: 'myIntegratedHealth' }))
  };

  const onChangeSubquestions = (value: string, questionNumber: number, inputType: string, subquestionsIndex: number) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestions({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType,
        subquestionsIndex
      })
    );
  };

  const onChangeSubquestion = (value: any, questionNumber: number, inputType: string) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestion({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType
      })
    );
  };

  const onChangeSubSubQuestion = (value: any, questionNumber: number, inputType: string) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestionOfSubquestion({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType
      })
    );
  };

  const onChangeSubquestionOfSubquestions = (value: string, questionNumber: number, inputType: string, subquestionsIndex: number) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestionOfSubquestions({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType,
        subquestionsIndex
      })
    );
  };

  const debouncedOnChange = debounce((value: string, questionNum: number, type: string, index: number) => {
    onChangeSubquestionOfSubquestions(value, questionNum, type, index);
  }, 1000);

  return (
    <>
      <StepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <QuestionsWrapper>
          <QuestionGroupTitle>C. Personality</QuestionGroupTitle>
          <QuestionWrapper>
            <label>C1. Resistence</label>
            {questions
              .filter((q) => q.question_type === 'C1. Resistence')
              .map((question) => {
                const subquestion = question?.subquestion;
                // const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`question_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>

                    {subquestion && showSubquestionTitle && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                            {subquestion.choices.some((choice: any) => choice.title === 'Other' && choice.selected) && subquestion.subquestion && (
                              subquestion.subquestion.type === 'input' && (
                                <OptionsWrapper key={`mobile_subsubquestion_${question.question_num}`}>
                                  <p>{subquestion.subquestion.label}</p>
                                  <Input
                                    defaultValue={subquestion.subquestion.value}
                                    onBlur={(e) => onChangeSubSubQuestion(e.target.value, question.question_num, subquestion.subquestion.type)}
                                    placeholder={subquestion.subquestion.placeholder ?? 'Write here...'}
                                  />
                                </OptionsWrapper>
                              )
                            )}
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>C2. Confidence</label>
            {questions
              .filter((q) => q.question_type === 'C2. Confidence')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        const showSubquestion = subq.showSubquestionValue === subq?.choices?.find((choice: any) => choice.selected)?.title;
                        return (
                          <>
                            {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                              <OptionsWrapper key={`mobile_subquestions_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <StyledRadioGroup
                                  onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                  optionType="button"
                                >
                                  <Space direction="vertical">
                                    {subq.choices.map((choice: any, index: number) => (
                                      <Radio
                                        key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                        value={choice.title}
                                      >
                                        {choice.title}
                                      </Radio>
                                    ))}
                                  </Space>
                                </StyledRadioGroup>
                                {showSubquestion && (
                                  subq?.subquestion?.type === 'input' && (
                                    <OptionsWrapper key={`mobile_subquestions_${question.question_num}`}>
                                      <p>{subq?.subquestion.label}</p>
                                      <Input
                                        defaultValue={subq?.subquestion.value}
                                        onChange={(e) => {
                                          debouncedOnChange(e.target.value, question.question_num, subq?.subquestion?.type, index);
                                        }}
                                        placeholder={subq?.subquestion.placeholder ?? 'Write here...'}
                                      />
                                    </OptionsWrapper>
                                  )
                                )}
                              </OptionsWrapper>
                            )}
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>C3. Social</label>
            {questions
              .filter((q) => q.question_type === 'C3. Social')
              .map((question) => {
                const subquestion = question?.subquestion;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                return (
                  <OptionsWrapper key={`question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`question_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>

                    {subquestion && showSubquestion && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>C4. Mental</label>
            {questions
              .filter((q) => q.question_type === 'C4. Mental')
              .map((question) => {
                const subquestion = question?.subquestion;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`question_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>

                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>C5. Emotions</label>
            {questions
              .filter((q) => q.question_type === 'C5. Emotions')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>C6. Creative</label>
            {questions
              .filter((q) => q.question_type === 'C6. Creative')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>C7. Impulsive</label>
            {questions
              .filter((q) => q.question_type === 'C7. Impulsive')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
        </QuestionsWrapper>
      </StepContainer>
      <DesktopStepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
        </p>
        <p>
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <PageWrapper>
          <span>Page {currentPage} of {totalPage}</span>
        </PageWrapper>
        <DesktopStepTitle>
          C. Personality
        </DesktopStepTitle>
        <DesktopQuestionWrapper>
          <label>C1. Resistence</label>
          {questions
            .filter((q) => q.question_type === 'C1. Resistence')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestion && showSubquestionTitle && (
                    <>
                      {subquestion.type === 'multi-checkbox' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <Checkbox.Group
                            onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                            defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                          >
                            <Space direction="vertical">
                              {subquestion.choices.map((choice: any, index: number) => (
                                <Checkbox key={`sub_${question.question_num}_${choice.title}_${index}`} value={choice.title}>{choice.title}</Checkbox>
                              ))}
                            </Space>
                          </Checkbox.Group>
                          {subquestion.choices.some((choice: any) => choice.title === 'Other' && choice.selected) && subquestion.subquestion && (
                            subquestion.subquestion.type === 'input' && (
                              <DesktopOptionsWrapper key={question.question_num}>
                                <p>{subquestion.subquestion.label}</p>
                                <Input
                                  defaultValue={subquestion.subquestion.value}
                                  onBlur={(e) => onChangeSubSubQuestion(e.target.value, question.question_num, subquestion.subquestion.type)}
                                  placeholder={subquestion.subquestion.placeholder ?? 'Write here...'}
                                />
                              </DesktopOptionsWrapper>
                            )
                          )}
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              );
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>C2. Confidence</label>
          {questions
            .filter((q) => q.question_type === 'C2. Confidence')
            .map((question) => {
              const subquestions = question?.subquestions;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`question_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestions && showSubquestionTitle && (
                    subquestions.map((subquestion: any) => {
                      return (
                        <>
                          {subquestion?.type === 'input' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subquestion?.label}</p>
                              <Input
                                defaultValue={subquestion?.value}
                                onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion?.type)}
                                placeholder={subquestion?.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                        </>
                      );
                    })
                  )}
                </DesktopOptionsWrapper>
              );
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>C3. Social</label>
          {questions
            .filter((q) => q.question_type === 'C3. Social')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
              return (
                <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestion && showSubquestion && (
                    <>
                      {subquestion?.type === 'input' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion?.label}</p>
                          <Input
                            defaultValue={subquestion?.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion?.type)}
                            placeholder={subquestion?.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              );
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>C4. Mental</label>
          {questions
            .filter((q) => q.question_type === 'C4. Mental')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestion = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestion && showSubquestion && (
                    <>
                      {subquestion?.type === 'input' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion?.label}</p>
                          <Input
                            defaultValue={subquestion?.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion?.type)}
                            placeholder={subquestion?.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              );
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>C5. Emotions</label>
          {questions
            .filter((q) => q.question_type === 'C5. Emotions')
            .map((question) => (
              <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction="horizontal">
                    {question.choices.map((choice: any, index: number) => (
                      <Radio
                        key={`subquestion_choice_${index}_${question.question_num}`}
                        value={choice.title}
                      >
                        {choice.title}
                      </Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                <Sublabels>
                  <p>{question.option_label_left}</p>
                  <p>{question.option_label_right}</p>
                </Sublabels>
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>C6. Creative</label>
          {questions
            .filter((q) => q.question_type === 'C6. Creative')
            .map((question) => (
              <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction="horizontal">
                    {question.choices.map((choice: any, index: number) => (
                      <Radio
                        key={`subquestion_choice_${index}_${question.question_num}`}
                        value={choice.title}
                      >
                        {choice.title}
                      </Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                <Sublabels>
                  <p>{question.option_label_left}</p>
                  <p>{question.option_label_right}</p>
                </Sublabels>
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>C7. Impulsive</label>
          {questions
            .filter((q) => q.question_type === 'C7. Impulsive')
            .map((question) => (
              <DesktopOptionsWrapper key={`desktop_question_${question.question_num}`}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction="horizontal">
                    {question.choices.map((choice: any, index: number) => (
                      <Radio
                        key={`subquestion_choice_${index}_${question.question_num}`}
                        value={choice.title}
                      >
                        {choice.title}
                      </Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                <Sublabels>
                  <p>{question.option_label_left}</p>
                  <p>{question.option_label_right}</p>
                </Sublabels>
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
      </DesktopStepContainer>
    </>
  )
};

const Step4 = () => {
  const { currentPage, questions, totalPage } = useSelector((state: RootState) => state.clientQuestionnaires.myIntegratedHealth);
  const dispatch = useDispatch();
  const onChangeRadio = (value: string, questionNumber: number) => {
    dispatch(clientQuestionnairesActions.toggleChoice({ value, questionNumber, type: 'myIntegratedHealth' }))
  };

  const onChangeSubquestion = (value: string, questionNumber: number, inputType: string) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestion({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType
      })
    );
  };

  const onChangeSubquestions = (value: string, questionNumber: number, inputType: string, subquestionsIndex: number) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestions({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType,
        subquestionsIndex
      })
    );
  };

  return (
    <>
      <StepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <QuestionsWrapper>
          <QuestionGroupTitle>D. Central Nervous system Nervous system</QuestionGroupTitle>
          <QuestionWrapper>
            <label>D1. General</label>
            {questions
              .filter((q) => q.question_type === 'D1. General')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>D2. Sleep</label>
            {questions
              .filter((q) => q.question_type === 'D2. Sleep')
              .map((question) => {
                const subquestion = question?.subquestion;
                // const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`question_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && showSubquestionTitle && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>D3. Perception</label>
            {questions
              .filter((q) => q.question_type === 'D3. Perception')
              .map((question) => {
                const subquestion = question?.subquestion;
                // const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`question_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && showSubquestionTitle && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>D4. Blood circulation</label>
            {questions
              .filter((q) => q.question_type === 'D4. Blood circulation')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      <>
                        <label>Which ones?</label>
                        {subquestions?.map((subq: any, index: number) => {
                          return (
                            <TableStyleContainer>
                              {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                                <TableOptionsWrapper key={question.question_num}>
                                  <p>{`${++index}. ${subq.label}`}</p>
                                  <DesktopStyledTableRadioGroup
                                    onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                    value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                    optionType="button"
                                  >
                                    <Space direction={'horizontal'}>
                                      {subq.choices.map((choice: any, index: number) => (
                                        <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                      ))}
                                    </Space>
                                  </DesktopStyledTableRadioGroup>
                                </TableOptionsWrapper>
                              )}
                            </TableStyleContainer>
                          )
                        })}
                      </>
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>D5. Immune function</label>
            {questions
              .filter((q) => q.question_type === 'D5. Immune function')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                        <p>Which ones?</p>
                        {subquestions?.map((subq: any, index: number) => {
                          return (
                            <TableStyleContainer>
                              {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                                <TableOptionsWrapper key={question.question_num}>
                                  <p>{`${++index}. ${subq.label}`}</p>
                                  <DesktopStyledTableRadioGroup
                                    onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                    value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                    optionType="button"
                                  >
                                    <Space direction={'horizontal'}>
                                      {subq.choices.map((choice: any, index: number) => (
                                        <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                      ))}
                                    </Space>
                                  </DesktopStyledTableRadioGroup>
                                </TableOptionsWrapper>
                              )}
                            </TableStyleContainer>
                          )
                        })}
                      </OptionsWrapper>
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>D6. Needs and addictions</label>
            {questions
              .filter((q) => q.question_type === 'D6. Needs and addictions')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
        </QuestionsWrapper>
      </StepContainer>
      <DesktopStepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
        </p>
        <p>
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <PageWrapper>
          <span>Page {currentPage} of {totalPage}</span>
        </PageWrapper>
        <DesktopStepTitle>
          D. Central Nervous system Nervous system
        </DesktopStepTitle>
        <DesktopQuestionWrapper>
          <label>D1. General</label>
          {questions
            .filter((q) => q.question_type === 'D1. General')
            .map((question) => {
              const subquestions = question?.subquestions;
              const showSubquestion = question.showSubquestionValue < question.choices.findIndex((choice: any) => choice.selected);
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction="horizontal">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestions && showSubquestion && (
                    subquestions?.map((subq: any, index: number) => {
                      return (
                        <>
                          {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <DesktopStyledRadioGroup
                                onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                optionType="button"
                              >
                                <Space direction={subq.type === 'radio' ? 'vertical' : 'horizontal'}>
                                  {subq.choices.map((choice: any, index: number) => (
                                    <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                  ))}
                                </Space>
                              </DesktopStyledRadioGroup>
                              {subq?.type === 'horizontal-radio' && (
                                <Sublabels>
                                  <p>{question.option_label_left}</p>
                                  <p>{question.option_label_right}</p>
                                </Sublabels>
                              )}
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'input' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                        </>
                      )
                    })
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>D2. Sleep</label>
          {questions
            .filter((q) => q.question_type === 'D2. Sleep')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestion && (showSubquestionTitle || showSubquestion) && (
                    <>
                      {subquestion.type === 'multi-checkbox' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <Checkbox.Group
                            onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                            defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                          >
                            <Space direction="vertical">
                              {subquestion.choices.map((choice: any, index: number) => (
                                <Checkbox key={`sub_${question.question_num}_${choice.title}_${index}`} value={choice.title}>{choice.title}</Checkbox>
                              ))}
                            </Space>
                          </Checkbox.Group>
                        </DesktopOptionsWrapper>
                      )}
                      {subquestion.type === 'textarea' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <Input
                            defaultValue={subquestion.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                            placeholder={subquestion.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                      {subquestion.type === 'input' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <Input
                            defaultValue={subquestion.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                            placeholder={subquestion.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                      {(subquestion?.type === 'radio' || subquestion?.type === 'horizontal-radio') && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <DesktopStyledRadioGroup
                            onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                            value={subquestion?.choices.find((choice: any) => choice.selected)?.[0]}
                            optionType="button"
                          >
                            <Space direction={subquestion.type === 'radio' ? 'vertical' : 'horizontal'}>
                              {subquestion.choices.map((choice: any, index: number) => (
                                <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                              ))}
                            </Space>
                          </DesktopStyledRadioGroup>
                          {subquestion?.type === 'horizontal-radio' && (
                            <Sublabels>
                              <p>{question.option_label_left}</p>
                              <p>{question.option_label_right}</p>
                            </Sublabels>
                          )}
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              );
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>D3. Perception</label>
          {questions
            .filter((q) => q.question_type === 'D3. Perception')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestion && (showSubquestionTitle || showSubquestion) && (
                    <>
                      {subquestion.type === 'textarea' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <Input
                            defaultValue={subquestion.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                            placeholder={subquestion.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                      {subquestion.type === 'input' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <Input
                            defaultValue={subquestion.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                            placeholder={subquestion.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              );
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>D4. Blood circulation</label>
          {questions
            .filter((q) => q.question_type === 'D4. Blood circulation')
            .map((question) => {
              const subquestions = question?.subquestions;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestions && showSubquestionTitle && (
                    subquestions?.map((subq: any, index: number) => {
                      return (
                        <>
                          {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <DesktopStyledRadioGroup
                                onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                optionType="button"
                              >
                                <Space direction={subq.type === 'radio' ? 'vertical' : 'horizontal'}>
                                  {subq.choices.map((choice: any, index: number) => (
                                    <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                  ))}
                                </Space>
                              </DesktopStyledRadioGroup>
                              {subq?.type === 'horizontal-radio' && (
                                <Sublabels>
                                  <p>{question.option_label_left}</p>
                                  <p>{question.option_label_right}</p>
                                </Sublabels>
                              )}
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'textarea' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'input' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                        </>
                      )
                    })
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>D5. Immune function</label>
          {questions
            .filter((q) => q.question_type === 'D5. Immune function')
            .map((question) => {
              const subquestions = question?.subquestions;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestions && showSubquestionTitle && (
                    <DesktopOptionsWrapper>
                      <label>Which ones?</label>
                      {subquestions?.map((subq: any, index: number) => {
                        return (
                          <TableStyleContainer>
                            {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                              <TableOptionsWrapper key={question.question_num}>
                                <p>{`${++index}. ${subq.label}`}</p>
                                <DesktopStyledTableRadioGroup
                                  onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                  optionType="button"
                                >
                                  <Space direction={'horizontal'}>
                                    {subq.choices.map((choice: any, index: number) => (
                                      <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                    ))}
                                  </Space>
                                </DesktopStyledTableRadioGroup>
                                {subq?.type === 'horizontal-radio' && (
                                  <Sublabels>
                                    <p>{question.option_label_left}</p>
                                    <p>{question.option_label_right}</p>
                                  </Sublabels>
                                )}
                              </TableOptionsWrapper>
                            )}
                          </TableStyleContainer>
                        )
                      })}
                    </DesktopOptionsWrapper>
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>D6. Needs and addictions</label>
          {questions
            .filter((q) => q.question_type === 'D6. Needs and addictions')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.findIndex((choice: any) => choice.selected)}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio value={index}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
      </DesktopStepContainer>
    </>
  )
};

const Step5 = () => {
  const { currentPage, questions, totalPage } = useSelector((state: RootState) => state.clientQuestionnaires.myIntegratedHealth);
  const dispatch = useDispatch();
  const onChangeRadio = (value: string, questionNumber: number) => {
    dispatch(clientQuestionnairesActions.toggleChoice({ value, questionNumber, type: 'myIntegratedHealth' }))
  };

  const onChangeSubquestions = (value: string, questionNumber: number, inputType: string, subquestionsIndex: number) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestions({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType,
        subquestionsIndex
      })
    );
  };

  const onChangeSubquestion = (value: any, questionNumber: number, inputType: string) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestion({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType
      })
    );
  };

  return (
    <>
      <StepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <QuestionsWrapper>
          <QuestionGroupTitle>E. Peripheral nervous system nervous system</QuestionGroupTitle>
          <QuestionWrapper>
            <label>E1. Pain</label>
            {questions
              .filter((q) => q.question_type === 'E1. Pain')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>E2. Autonomic nervous system</label>
            {questions
              .filter((q) => q.question_type === 'E2. Autonomic nervous system')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>E3. Somatic Nervous System</label>
            {questions
              .filter((q) => q.question_type === 'E3. Somatic Nervous System')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                        <p>Which ones?</p>
                        {subquestions?.map((subq: any, index: number) => {
                          return (
                            <TableStyleContainer>
                              {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                                <TableOptionsWrapper key={question.question_num}>
                                  <p>{`${++index}. ${subq.label}`}</p>
                                  <DesktopStyledTableRadioGroup
                                    onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                    value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                    optionType="button"
                                  >
                                    <Space direction={'horizontal'}>
                                      {subq.choices.map((choice: any, index: number) => (
                                        <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                      ))}
                                    </Space>
                                  </DesktopStyledTableRadioGroup>
                                </TableOptionsWrapper>
                              )}
                            </TableStyleContainer>
                          )
                        })}
                      </OptionsWrapper>
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>E4. Hearing</label>
            {questions
              .filter((q) => q.question_type === 'E4. Hearing')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>E5. Seeing</label>
            {questions
              .filter((q) => q.question_type === 'E5. Seeing')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>E6. Smelling</label>
            {questions
              .filter((q) => q.question_type === 'E6. Smelling')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>E7. Sense of touch</label>
            {questions
              .filter((q) => q.question_type === 'E7. Sense of touch')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>E8. Balance</label>
            {questions
              .filter((q) => q.question_type === 'E8. Balance')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
        </QuestionsWrapper>
      </StepContainer>
      <DesktopStepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
        </p>
        <p>
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <PageWrapper>
          <span>Page {currentPage} of {totalPage}</span>
        </PageWrapper>
        <DesktopStepTitle>
          E. Peripheral nervous system nervous system
        </DesktopStepTitle>
        <DesktopQuestionWrapper>
          <label>E1. Pain</label>
          {questions
            .filter((q) => q.question_type === 'E1. Pain')
            .map((question) => {
              const subquestions = question?.subquestions;
              const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestions && showSubquestion && (
                    subquestions?.map((subq: any, index: number) => {
                      return (
                        <>
                          {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <DesktopStyledRadioGroup
                                onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                optionType="button"
                              >
                                <Space direction={subq.type === 'radio' ? 'vertical' : 'horizontal'}>
                                  {subq.choices.map((choice: any, index: number) => (
                                    <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                  ))}
                                </Space>
                              </DesktopStyledRadioGroup>
                              {subq?.type === 'horizontal-radio' && (
                                <Sublabels>
                                  <p>{question.option_label_left}</p>
                                  <p>{question.option_label_right}</p>
                                </Sublabels>
                              )}
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'textarea' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'input' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                        </>
                      )
                    })
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>E2. Autonomic nervous system</label>
          {questions
            .filter((q) => q.question_type === 'E2. Autonomic nervous system')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>E3. Somatic Nervous System</label>
          {questions
            .filter((q) => q.question_type === 'E3. Somatic Nervous System')
            .map((question) => {
              const subquestions = question?.subquestions;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestions && showSubquestionTitle && (
                    <DesktopOptionsWrapper>
                      <label>Which ones?</label>
                      {subquestions?.map((subq: any, index: number) => {
                        return (
                          <TableStyleContainer>
                            {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                              <TableOptionsWrapper key={question.question_num}>
                                <p>{`${++index}. ${subq.label}`}</p>
                                <DesktopStyledTableRadioGroup
                                  onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                  optionType="button"
                                >
                                  <Space direction={'horizontal'}>
                                    {subq.choices.map((choice: any, index: number) => (
                                      <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                    ))}
                                  </Space>
                                </DesktopStyledTableRadioGroup>
                                {subq?.type === 'horizontal-radio' && (
                                  <Sublabels>
                                    <p>{question.option_label_left}</p>
                                    <p>{question.option_label_right}</p>
                                  </Sublabels>
                                )}
                              </TableOptionsWrapper>
                            )}
                          </TableStyleContainer>
                        )
                      })}
                    </DesktopOptionsWrapper>
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>E4. Hearing</label>
          {questions
            .filter((q) => q.question_type === 'E4. Hearing')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>E5. Seeing</label>
          {questions
            .filter((q) => q.question_type === 'E5. Seeing')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestion && showSubquestionTitle && (
                    <>
                      {subquestion?.type === 'input' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion?.label}</p>
                          <Input
                            defaultValue={subquestion?.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion?.type)}
                            placeholder={subquestion?.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              );
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>E6. Smelling</label>
          {questions
            .filter((q) => q.question_type === 'E6. Smelling')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>E7. Sense of touch</label>
          {questions
            .filter((q) => q.question_type === 'E7. Sense of touch')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>E8. Balance</label>
          {questions
            .filter((q) => q.question_type === 'E8. Balance')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
      </DesktopStepContainer>
    </>
  )
};

const Step6 = () => {
  const { currentPage, questions, totalPage } = useSelector((state: RootState) => state.clientQuestionnaires.myIntegratedHealth);
  const dispatch = useDispatch();
  const onChangeRadio = (value: string, questionNumber: number) => {
    dispatch(clientQuestionnairesActions.toggleChoice({ value, questionNumber, type: 'myIntegratedHealth' }))
  };

  return (
    <>
      <StepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <QuestionsWrapper>
          <QuestionGroupTitle>F. Locomotor system system</QuestionGroupTitle>
          <QuestionWrapper>
            <label>F1. Muscles</label>
            {questions
              .filter((q) => q.question_type === 'F1. Muscles')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>F2. Joints</label>
            {questions
              .filter((q) => q.question_type === 'F2. Joints')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>F3. Bones</label>
            {questions
              .filter((q) => q.question_type === 'F3. Bones')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>F4. Tendons and connective tissue</label>
            {questions
              .filter((q) => q.question_type === 'F4. Tendons and connective tissue')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
        </QuestionsWrapper>
      </StepContainer>
      <DesktopStepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
        </p>
        <p>
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <PageWrapper>
          <span>Page {currentPage} of {totalPage}</span>
        </PageWrapper>
        <DesktopStepTitle>
          F. Locomotor system system
        </DesktopStepTitle>
        <DesktopQuestionWrapper>
          <label>F1. Muscles</label>
          {questions
            .filter((q) => q.question_type === 'F1. Muscles')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>F2. Joints</label>
          {questions
            .filter((q) => q.question_type === 'F2. Joints')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>F3. Bones</label>
          {questions
            .filter((q) => q.question_type === 'F3. Bones')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>F4. Tendons and connective tissue</label>
          {questions
            .filter((q) => q.question_type === 'F4. Tendons and connective tissue')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
      </DesktopStepContainer>
    </>
  )
};

const Step7 = () => {
  const { currentPage, questions, totalPage } = useSelector((state: RootState) => state.clientQuestionnaires.myIntegratedHealth);
  const dispatch = useDispatch();

  const onChangeRadio = (value: string, questionNumber: number) => {
    dispatch(clientQuestionnairesActions.toggleChoice({ value, questionNumber, type: 'myIntegratedHealth' }))
  };

  const onChangeSubquestions = (value: string, questionNumber: number, inputType: string, subquestionsIndex: number) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestions({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType,
        subquestionsIndex
      })
    );
  };

  const onChangeSubquestion = (value: any, questionNumber: number, inputType: string) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestion({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType
      })
    );
  };

  return (
    <>
      <StepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <QuestionsWrapper>
          <QuestionGroupTitle>G. Neurohormonal system system</QuestionGroupTitle>
          <QuestionWrapper>
            <label>G1. HPA-axis</label>
            {questions
              .filter((q) => q.question_type === 'G1. HPA-axis')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>G2. HPT axis</label>
            {questions
              .filter((q) => q.question_type === 'G2. HPT axis')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>G4. HPG axis (female)</label>
            {questions
              .filter((q) => q.question_type === 'G4. HPG axis (female)')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      <OptionsWrapper>
                        <p>Which ones?</p>
                        {subquestions?.map((subq: any, index: number) => {
                          return (
                            <TableStyleContainer>
                              {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                                <TableOptionsWrapper key={question.question_num}>
                                  <p>{`${++index}. ${subq.label}`}</p>
                                  <DesktopStyledTableRadioGroup
                                    onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                    value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                    optionType="button"
                                  >
                                    <Space direction={'horizontal'}>
                                      {subq.choices.map((choice: any, index: number) => (
                                        <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                      ))}
                                    </Space>
                                  </DesktopStyledTableRadioGroup>
                                </TableOptionsWrapper>
                              )}
                            </TableStyleContainer>
                          )
                        })}
                      </OptionsWrapper>
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>G5. GH/IGF-1 axis</label>
            {questions
              .filter((q) => q.question_type === 'G5. GH/IGF-1 axis')
              .map((question) => (
                <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
        </QuestionsWrapper>
      </StepContainer>
      <DesktopStepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
        </p>
        <p>
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <PageWrapper>
          <span>Page {currentPage} of {totalPage}</span>
        </PageWrapper>
        <DesktopStepTitle>
          G. Neurohormonal system system
        </DesktopStepTitle>
        <DesktopQuestionWrapper>
          <label>G1. HPA-axis</label>
          {questions
            .filter((q) => q.question_type === 'G1. HPA-axis')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>G2. HPT axis</label>
          {questions
            .filter((q) => q.question_type === 'G2. HPT axis')
            .map((question) => {
              const subquestions = question?.subquestions;
              const subquestion = question?.subquestion;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestions && showSubquestionTitle && (
                    <DesktopOptionsWrapper>
                      <label>Which ones?</label>
                      {subquestions?.map((subq: any, index: number) => {
                        return (
                          <TableStyleContainer>
                            {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                              <TableOptionsWrapper key={question.question_num}>
                                <p>{`${++index}. ${subq.label}`}</p>
                                <DesktopStyledTableRadioGroup
                                  onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                  optionType="button"
                                >
                                  <Space direction={'horizontal'}>
                                    {subq.choices.map((choice: any, index: number) => (
                                      <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                    ))}
                                  </Space>
                                </DesktopStyledTableRadioGroup>
                                {subq?.type === 'horizontal-radio' && (
                                  <Sublabels>
                                    <p>{question.option_label_left}</p>
                                    <p>{question.option_label_right}</p>
                                  </Sublabels>
                                )}
                              </TableOptionsWrapper>
                            )}
                          </TableStyleContainer>
                        )
                      })}
                    </DesktopOptionsWrapper>
                  )}
                  {subquestion && showSubquestionTitle && (
                    <>
                      {subquestion?.type === 'input' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion?.label}</p>
                          <Input
                            defaultValue={subquestion?.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion?.type)}
                            placeholder={subquestion?.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>G4. HPG axis (female)</label>
          {questions
            .filter((q) => q.question_type === 'G4. HPG axis (female)')
            .map((question) => {
              const subquestions = question?.subquestions;
              const subquestion = question?.subquestion;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestions && showSubquestionTitle && (
                    <DesktopOptionsWrapper>
                      <label>Which ones?</label>
                      {subquestions?.map((subq: any, index: number) => {
                        return (
                          <TableStyleContainer>
                            {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                              <TableOptionsWrapper key={question.question_num}>
                                <p>{`${++index}. ${subq.label}`}</p>
                                <DesktopStyledTableRadioGroup
                                  onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                  optionType="button"
                                >
                                  <Space direction={'horizontal'}>
                                    {subq.choices.map((choice: any, index: number) => (
                                      <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                    ))}
                                  </Space>
                                </DesktopStyledTableRadioGroup>
                                {subq?.type === 'horizontal-radio' && (
                                  <Sublabels>
                                    <p>{question.option_label_left}</p>
                                    <p>{question.option_label_right}</p>
                                  </Sublabels>
                                )}
                              </TableOptionsWrapper>
                            )}
                          </TableStyleContainer>
                        )
                      })}
                    </DesktopOptionsWrapper>
                  )}
                  {subquestion && showSubquestionTitle && (
                    <>
                      {subquestion?.type === 'input' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion?.label}</p>
                          <Input
                            defaultValue={subquestion?.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion?.type)}
                            placeholder={subquestion?.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>G5. GH/IGF-1 axis</label>
          {questions
            .filter((q) => q.question_type === 'G5. GH/IGF-1 axis')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
      </DesktopStepContainer>
    </>
  )
};

const Step8 = () => {
  const { currentPage, questions, totalPage } = useSelector((state: RootState) => state.clientQuestionnaires.myIntegratedHealth);
  const dispatch = useDispatch();
  const onChangeRadio = (value: string, questionNumber: number) => {
    dispatch(clientQuestionnairesActions.toggleChoice({ value, questionNumber, type: 'myIntegratedHealth' }))
  };

  const onChangeSubquestion = (value: string, questionNumber: number, inputType: string) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestion({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType
      })
    );
  };

  const onChangeSubquestions = (value: string, questionNumber: number, inputType: string, subquestionsIndex: number) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestions({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType,
        subquestionsIndex
      })
    );
  };

  return (
    <>
      <StepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <QuestionsWrapper>
          <QuestionGroupTitle>H. Cardiovascular system system</QuestionGroupTitle>
          <QuestionWrapper>
            <label>H1. Blood values</label>
            {questions
              .filter((q) => q.question_type === 'H1. Blood values')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>H2. Blood pressure</label>
            {questions
              .filter((q) => q.question_type === 'H2. Blood pressure')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>H3. Heartbeat</label>
            {questions
              .filter((q) => q.question_type === 'H3. Heartbeat')
              .map((question) => (
                <OptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.[0]}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio
                          key={`subquestion_choice_${index}_${question.question_num}`}
                          value={choice.title}
                        >
                          {choice.title}
                        </Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
        </QuestionsWrapper>
      </StepContainer>
      <DesktopStepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
        </p>
        <p>
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <PageWrapper>
          <span>Page {currentPage} of {totalPage}</span>
        </PageWrapper>
        <DesktopStepTitle>
          H. Cardiovascular system system
        </DesktopStepTitle>
        <DesktopQuestionWrapper>
          <label>H1. Blood values</label>
          {questions
            .filter((q) => q.question_type === 'H1. Blood values')
            .map((question) => {
              const subquestions = question?.subquestions;
              const subquestion = question?.subquestion;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestions && showSubquestionTitle && (
                    subquestions?.map((subq: any, index: number) => {
                      return (
                        <>
                          {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <DesktopStyledRadioGroup
                                onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                optionType="button"
                              >
                                <Space direction={subq.type === 'radio' ? 'vertical' : 'horizontal'}>
                                  {subq.choices.map((choice: any, index: number) => (
                                    <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                  ))}
                                </Space>
                              </DesktopStyledRadioGroup>
                              {subq?.type === 'horizontal-radio' && (
                                <Sublabels>
                                  <p>{question.option_label_left}</p>
                                  <p>{question.option_label_right}</p>
                                </Sublabels>
                              )}
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'textarea' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'input' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                        </>
                      )
                    })
                  )}
                  {subquestion && showSubquestionTitle && (
                    <>
                      {subquestion?.type === 'input' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion?.label}</p>
                          <Input
                            defaultValue={subquestion?.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion?.type)}
                            placeholder={subquestion?.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>H2. Blood pressure</label>
          {questions
            .filter((q) => q.question_type === 'H2. Blood pressure')
            .map((question) => {
              const subquestions = question?.subquestions;
              const subquestion = question?.subquestion;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestions && showSubquestion && (
                    subquestions?.map((subq: any, index: number) => {
                      return (
                        <>
                          {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <DesktopStyledRadioGroup
                                onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                optionType="button"
                              >
                                <Space direction={subq.type === 'radio' ? 'vertical' : 'horizontal'}>
                                  {subq.choices.map((choice: any, index: number) => (
                                    <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                  ))}
                                </Space>
                              </DesktopStyledRadioGroup>
                              {subq?.type === 'horizontal-radio' && (
                                <Sublabels>
                                  <p>{question.option_label_left}</p>
                                  <p>{question.option_label_right}</p>
                                </Sublabels>
                              )}
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'textarea' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'input' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                        </>
                      )
                    })
                  )}
                  {subquestion && showSubquestionTitle && (
                    <>
                      {subquestion?.type === 'input' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion?.label}</p>
                          <Input
                            defaultValue={subquestion?.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion?.type)}
                            placeholder={subquestion?.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>H3. Heartbeat</label>
          {questions
            .filter((q) => q.question_type === 'H3. Heartbeat')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
      </DesktopStepContainer>
    </>
  )
};

const Step9 = () => {
  const { currentPage, questions, totalPage } = useSelector((state: RootState) => state.clientQuestionnaires.myIntegratedHealth);
  const dispatch = useDispatch();
  const onChangeRadio = (value: string, questionNumber: number) => {
    dispatch(clientQuestionnairesActions.toggleChoice({ value, questionNumber, type: 'myIntegratedHealth' }))
  };

  const onChangeSubquestions = (value: string, questionNumber: number, inputType: string, subquestionsIndex: number) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestions({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType,
        subquestionsIndex
      })
    );
  };

  const onChangeSubquestion = (value: any, questionNumber: number, inputType: string) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestion({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType
      })
    );
  };

  return (
    <>
      <StepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <QuestionsWrapper>
          <QuestionGroupTitle>I. Resistance</QuestionGroupTitle>
          <QuestionWrapper>
            <label>I1. Skin</label>
            {questions
              .filter((q) => q.question_type === 'I1. Skin')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>I2. Throat / nose / ears</label>
            {questions
              .filter((q) => q.question_type === 'I2. Throat / nose / ears')
              .map((question) => (
                <OptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.findIndex((choice: any) => choice.selected)}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio value={index}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>I3. Respiratory system</label>
            {questions
              .filter((q) => q.question_type === 'I3. Respiratory system')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>I4. Urinary tract</label>
            {questions
              .filter((q) => q.question_type === 'I4. Urinary tract')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>I5. Mucous membranes</label>
            {questions
              .filter((q) => q.question_type === 'I5. Mucous membranes')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>I6. Immune System</label>
            {questions
              .filter((q) => q.question_type === 'I6. Immune System')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
        </QuestionsWrapper>
      </StepContainer>
      <DesktopStepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
        </p>
        <p>
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <PageWrapper>
          <span>Page {currentPage} of {totalPage}</span>
        </PageWrapper>
        <DesktopStepTitle>
          I. Resistance
        </DesktopStepTitle>
        <DesktopQuestionWrapper>
          <label>I1. Skin</label>
          {questions
            .filter((q) => q.question_type === 'I1. Skin')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestion && showSubquestion && (
                    <>
                      {subquestion?.type === 'input' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion?.label}</p>
                          <Input
                            defaultValue={subquestion?.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion?.type)}
                            placeholder={subquestion?.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              );
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>I2. Throat / nose / ears</label>
          {questions
            .filter((q) => q.question_type === 'I2. Throat / nose / ears')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>I3. Respiratory system</label>
          {questions
            .filter((q) => q.question_type === 'I3. Respiratory system')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestion && showSubquestionTitle && (
                    <>
                      {subquestion.type === 'multi-checkbox' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <Checkbox.Group
                            onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                            defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                          >
                            <Space direction="vertical">
                              {subquestion.choices.map((choice: any, index: number) => (
                                <Checkbox key={`sub_${question.question_num}_${choice.title}_${index}`} value={choice.title}>{choice.title}</Checkbox>
                              ))}
                            </Space>
                          </Checkbox.Group>
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              );
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>I4. Urinary tract</label>
          {questions
            .filter((q) => q.question_type === 'I4. Urinary tract')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestion && showSubquestion && (
                    <>
                      {subquestion?.type === 'input' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion?.label}</p>
                          <Input
                            defaultValue={subquestion?.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion?.type)}
                            placeholder={subquestion?.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              );
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>I5. Mucous membranes</label>
          {questions
            .filter((q) => q.question_type === 'I5. Mucous membranes')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestion && showSubquestionTitle && (
                    <>
                      {subquestion.type === 'multi-checkbox' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <Checkbox.Group
                            onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                            defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                          >
                            <Space direction="vertical">
                              {subquestion.choices.map((choice: any, index: number) => (
                                <Checkbox key={`sub_${question.question_num}_${choice.title}_${index}`} value={choice.title}>{choice.title}</Checkbox>
                              ))}
                            </Space>
                          </Checkbox.Group>
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              );
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>I6. Immune System</label>
          {questions
            .filter((q) => q.question_type === 'I6. Immune System')
            .map((question) => {
              const subquestions = question?.subquestions;
              const subquestion = question?.subquestion;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestions && showSubquestionTitle && (
                    subquestions?.map((subq: any, index: number) => {
                      return (
                        <>
                          {(subq?.type === 'radio' || subq?.type === 'horizontal-radio' || subq?.type === 'radio-vertical') && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <DesktopStyledRadioGroup
                                onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                optionType="button"
                              >
                                <Space direction={(subq.type === 'radio' || subq.type === 'radio-vertical') ? 'vertical' : 'horizontal'}>
                                  {subq.choices.map((choice: any, index: number) => (
                                    <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                  ))}
                                </Space>
                              </DesktopStyledRadioGroup>
                              {subq?.type === 'horizontal-radio' && (
                                <Sublabels>
                                  <p>{question.option_label_left}</p>
                                  <p>{question.option_label_right}</p>
                                </Sublabels>
                              )}
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'textarea' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'input' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                          {subq.type === 'multi-checkbox' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Checkbox.Group
                                onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subq.type)}
                                defaultValue={subq.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                              >
                                <Space direction="vertical">
                                  {subq.choices.map((choice: any, index: number) => (
                                    <Checkbox key={`sub_${question.question_num}_${choice.title}_${index}`} value={choice.title}>{choice.title}</Checkbox>
                                  ))}
                                </Space>
                              </Checkbox.Group>
                            </DesktopOptionsWrapper>
                          )}
                        </>
                      )
                    })
                  )}
                  {subquestion && showSubquestionTitle && (
                    <>
                      {subquestion?.type === 'input' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion?.label}</p>
                          <Input
                            defaultValue={subquestion?.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion?.type)}
                            placeholder={subquestion?.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                      {(subquestion?.type === 'radio' || subquestion?.type === 'horizontal-radio' || subquestion?.type === 'radio-vertical') && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <DesktopStyledRadioGroup
                            onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                            value={subquestion?.choices.find((choice: any) => choice.selected)?.[0]}
                            optionType="button"
                          >
                            <Space direction={(subquestion.type === 'radio' || subquestion.type === 'radio-vertical') ? 'vertical' : 'horizontal'}>
                              {subquestion.choices.map((choice: any, index: number) => (
                                <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                              ))}
                            </Space>
                          </DesktopStyledRadioGroup>
                          {subquestion?.type === 'horizontal-radio' && (
                            <Sublabels>
                              <p>{question.option_label_left}</p>
                              <p>{question.option_label_right}</p>
                            </Sublabels>
                          )}
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
      </DesktopStepContainer>
    </>
  )
};

const Step10 = () => {
  const { currentPage, questions, totalPage } = useSelector((state: RootState) => state.clientQuestionnaires.myIntegratedHealth);
  const dispatch = useDispatch();
  const onChangeRadio = (value: string, questionNumber: number) => {
    dispatch(clientQuestionnairesActions.toggleChoice({ value, questionNumber, type: 'myIntegratedHealth' }))
  };

  const onChangeSubquestion = (value: any, questionNumber: number, inputType: string) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestion({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType
      })
    );
  };

  const onChangeSubquestions = (value: string, questionNumber: number, inputType: string, subquestionsIndex: number) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestions({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType,
        subquestionsIndex
      })
    );
  };

  const onChangeSubquestionOfSubquestions = (value: string, questionNumber: number, inputType: string, subquestionsIndex: number) => {
    dispatch(
      clientQuestionnairesActions.updateSubquestionOfSubquestions({
        value,
        questionNumber,
        type: 'myIntegratedHealth',
        inputType,
        subquestionsIndex
      })
    );
  };

  const debouncedOnChange = debounce((value: string, questionNum: number, type: string, index: number) => {
    onChangeSubquestionOfSubquestions(value, questionNum, type, index);
  }, 1000);

  return (
    <>
      <StepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <QuestionsWrapper>
          <QuestionGroupTitle>J. Digestion</QuestionGroupTitle>
          <QuestionWrapper>
            <label>J1. General</label>
            {questions
              .filter((q) => q.question_type === 'J1. General')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>J2. Pain</label>
            {questions
              .filter((q) => q.question_type === 'J2. Pain')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>J3. Digestion</label>
            {questions
              .filter((q) => q.question_type === 'J3. Digestion')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>J4. Stool</label>
            {questions
              .filter((q) => q.question_type === 'J4. Stool')
              .map((question) => (
                <OptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.findIndex((choice: any) => choice.selected)}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio value={index}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>J5. Hunger</label>
            {questions
              .filter((q) => q.question_type === 'J5. Hunger')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>J6. Intolerance / Allergy</label>
            {questions
              .filter((q) => q.question_type === 'J6. Intolerance / Allergy')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        return (
                          <>
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>J7. Gut immune function</label>
            {questions
              .filter((q) => q.question_type === 'J7. Gut immune function')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        const showSubquestion = subq.showSubquestionValue === subq?.choices?.find((choice: any) => choice.selected)?.title;
                        return (
                          <>
                            {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                              <OptionsWrapper key={`mobile_subquestions_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <StyledRadioGroup
                                  onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                  optionType="button"
                                >
                                  <Space direction="vertical">
                                    {subq.choices.map((choice: any, index: number) => (
                                      <Radio
                                        key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                        value={choice.title}
                                      >
                                        {choice.title}
                                      </Radio>
                                    ))}
                                  </Space>
                                </StyledRadioGroup>
                                {showSubquestion && (
                                  subq?.subquestion?.type === 'input' && (
                                    <OptionsWrapper key={`mobile_subquestions_${question.question_num}`}>
                                      <p>{subq?.subquestion.label}</p>
                                      <Input
                                        defaultValue={subq?.subquestion.value}
                                        onChange={(e) => {
                                          debouncedOnChange(e.target.value, question.question_num, subq?.subquestion?.type, index);
                                        }}
                                        placeholder={subq?.subquestion.placeholder ?? 'Write here...'}
                                      />
                                    </OptionsWrapper>
                                  )
                                )}
                              </OptionsWrapper>
                            )}
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
          <QuestionWrapper>
            <label>J8. Physical - general</label>
            {questions
              .filter((q) => q.question_type === 'J8. Physical - general')
              .map((question) => {
                const subquestion = question?.subquestion;
                const subquestions = question?.subquestions;
                const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
                const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
                return (
                  <OptionsWrapper key={`mobile_question_${question.question_num}`}>
                    <p>{question.label}</p>
                    <StyledRadioGroup
                      onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                      value={question.choices.find((choice: any) => choice.selected)?.[0]}
                      optionType="button"
                    >
                      <Space direction="vertical">
                        {question.choices.map((choice: any, index: number) => (
                          <Radio
                            key={`subquestion_choice_${index}_${question.question_num}`}
                            value={choice.title}
                          >
                            {choice.title}
                          </Radio>
                        ))}
                      </Space>
                    </StyledRadioGroup>
                    {subquestion && (showSubquestion || showSubquestionTitle) && (
                      <>
                        {subquestion.type === 'radio' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <StyledRadioGroup
                              onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              value={subquestion.choices.find((choice: any) => choice.selected)?.[0]}
                              optionType="button"
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Radio
                                    key={`subquestion_choice_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Radio>
                                ))}
                              </Space>
                            </StyledRadioGroup>
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'input' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Input
                              defaultValue={subquestion.value}
                              onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                              placeholder={subquestion.placeholder ?? 'Write here...'}
                            />
                          </OptionsWrapper>
                        )}
                        {subquestion.type === 'multi-checkbox' && (
                          <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                            <p>{subquestion.label}</p>
                            <Checkbox.Group
                              onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                              defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                            >
                              <Space direction="vertical">
                                {subquestion.choices.map((choice: any, index: number) => (
                                  <Checkbox
                                    key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                    value={choice.title}
                                  >
                                    {choice.title}
                                  </Checkbox>
                                ))}
                              </Space>
                            </Checkbox.Group>
                          </OptionsWrapper>
                        )}
                      </>
                    )}
                    {subquestions && (showSubquestion || showSubquestionTitle) && (
                      subquestions?.map((subq: any, index: number) => {
                        const showSubquestion = subq.showSubquestionValue === subq?.choices?.find((choice: any) => choice.selected)?.title;
                        return (
                          <>
                            {(subq?.type === 'radio' || subq?.type === 'horizontal-radio') && (
                              <OptionsWrapper key={`mobile_subquestions_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <StyledRadioGroup
                                  onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                  optionType="button"
                                >
                                  <Space direction="vertical">
                                    {subq.choices.map((choice: any, index: number) => (
                                      <Radio
                                        key={`subquestion_choice_${choice.title}_${index}_${question.question_num}`}
                                        value={choice.title}
                                      >
                                        {choice.title}
                                      </Radio>
                                    ))}
                                  </Space>
                                </StyledRadioGroup>
                                {showSubquestion && (
                                  subq?.subquestion?.type === 'input' && (
                                    <OptionsWrapper key={`mobile_subquestions_${question.question_num}`}>
                                      <p>{subq?.subquestion.label}</p>
                                      <Input
                                        defaultValue={subq?.subquestion.value}
                                        onChange={(e) => {
                                          debouncedOnChange(e.target.value, question.question_num, subq?.subquestion?.type, index);
                                        }}
                                        placeholder={subq?.subquestion.placeholder ?? 'Write here...'}
                                      />
                                    </OptionsWrapper>
                                  )
                                )}
                              </OptionsWrapper>
                            )}
                            {subq?.type === 'input' && (
                              <OptionsWrapper key={`mobile_subquestion_${question.question_num}`}>
                                <p>{subq.label}</p>
                                <Input
                                  defaultValue={subq.value}
                                  onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                  placeholder={subq.placeholder ?? 'Write here...'}
                                />
                              </OptionsWrapper>
                            )}
                          </>
                        )
                      })
                    )}
                  </OptionsWrapper>
                )
              })
            }
          </QuestionWrapper>
        </QuestionsWrapper>
      </StepContainer>
      <DesktopStepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
        </p>
        <p>
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <PageWrapper>
          <span>Page {currentPage} of {totalPage}</span>
        </PageWrapper>
        <DesktopStepTitle>
          J. Digestion
        </DesktopStepTitle>
        <DesktopQuestionWrapper>
          <label>J1. General</label>
          {questions
            .filter((q) => q.question_type === 'J1. General')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>J2. Pain</label>
          {questions
            .filter((q) => q.question_type === 'J2. Pain')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>J3. Digestion</label>
          {questions
            .filter((q) => q.question_type === 'J3. Digestion')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestion && showSubquestion && (
                    <>
                      {subquestion.type === 'multi-checkbox' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <Checkbox.Group
                            onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                            defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                          >
                            <Space direction="vertical">
                              {subquestion.choices.map((choice: any, index: number) => (
                                <Checkbox key={`sub_${question.question_num}_${choice.title}_${index}`} value={choice.title}>{choice.title}</Checkbox>
                              ))}
                            </Space>
                          </Checkbox.Group>
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              );
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>J4. Stool</label>
          {questions
            .filter((q) => q.question_type === 'J4. Stool')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>J5. Hunger</label>
          {questions
            .filter((q) => q.question_type === 'J5. Hunger')
            .map((question) => {
              const subquestion = question?.subquestion;
              const showSubquestion = question.showSubquestionValue < parseInt(question.choices.find((choice: any) => choice.selected)?.title);
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestion && showSubquestion && (
                    <>
                      {subquestion.type === 'multi-checkbox' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <Checkbox.Group
                            onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subquestion.type)}
                            defaultValue={subquestion.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                          >
                            <Space direction="vertical">
                              {subquestion.choices.map((choice: any, index: number) => (
                                <Checkbox key={`sub_${question.question_num}_${choice.title}_${index}`} value={choice.title}>{choice.title}</Checkbox>
                              ))}
                            </Space>
                          </Checkbox.Group>
                        </DesktopOptionsWrapper>
                      )}
                      {subquestion.type === 'input' && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <Input
                            defaultValue={subquestion.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                            placeholder={subquestion.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              );
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>J6. Intolerance / Allergy</label>
          {questions
            .filter((q) => q.question_type === 'J6. Intolerance / Allergy')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>J7. Gut immune function</label>
          {questions
            .filter((q) => q.question_type === 'J7. Gut immune function')
            .map((question) => {
              const subquestions = question?.subquestions;
              const subquestion = question?.subquestion;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestions && showSubquestionTitle && (
                    subquestions?.map((subq: any, index: number) => {
                      return (
                        <>
                          {(subq?.type === 'radio' || subq?.type === 'horizontal-radio' || subq?.type === 'radio-vertical') && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <DesktopStyledRadioGroup
                                onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                optionType="button"
                              >
                                <Space direction={(subq.type === 'radio' || subq.type === 'radio-vertical') ? 'vertical' : 'horizontal'}>
                                  {subq.choices.map((choice: any, index: number) => (
                                    <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                  ))}
                                </Space>
                              </DesktopStyledRadioGroup>
                              {subq?.type === 'horizontal-radio' && (
                                <Sublabels>
                                  <p>{question.option_label_left}</p>
                                  <p>{question.option_label_right}</p>
                                </Sublabels>
                              )}
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'textarea' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'input' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                          {subq.type === 'multi-checkbox' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Checkbox.Group
                                onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subq.type)}
                                defaultValue={subq.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                              >
                                <Space direction="vertical">
                                  {subq.choices.map((choice: any, index: number) => (
                                    <Checkbox key={`sub_${question.question_num}_${choice.title}_${index}`} value={choice.title}>{choice.title}</Checkbox>
                                  ))}
                                </Space>
                              </Checkbox.Group>
                            </DesktopOptionsWrapper>
                          )}
                        </>
                      )
                    })
                  )}
                  {subquestion && showSubquestionTitle && (
                    <>
                      {(subquestion?.type === 'input' || subquestion?.type === 'textarea') && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion?.label}</p>
                          <Input
                            defaultValue={subquestion?.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion?.type)}
                            placeholder={subquestion?.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                      {(subquestion?.type === 'radio' || subquestion?.type === 'horizontal-radio' || subquestion?.type === 'radio-vertical') && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <DesktopStyledRadioGroup
                            onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                            value={subquestion?.choices.find((choice: any) => choice.selected)?.[0]}
                            optionType="button"
                          >
                            <Space direction={(subquestion.type === 'radio' || subquestion.type === 'radio-vertical') ? 'vertical' : 'horizontal'}>
                              {subquestion.choices.map((choice: any, index: number) => (
                                <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                              ))}
                            </Space>
                          </DesktopStyledRadioGroup>
                          {subquestion?.type === 'horizontal-radio' && (
                            <Sublabels>
                              <p>{question.option_label_left}</p>
                              <p>{question.option_label_right}</p>
                            </Sublabels>
                          )}
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
        <DesktopQuestionWrapper>
          <label>J8. Physical - general</label>
          {questions
            .filter((q) => q.question_type === 'J8. Physical - general')
            .map((question) => {
              const subquestions = question?.subquestions;
              const subquestion = question?.subquestion;
              const showSubquestionTitle = question.showSubquestionValue === question.choices.find((choice: any) => choice.selected)?.title;
              return (
                <DesktopOptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <DesktopStyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.find((choice: any) => choice.selected)?.title}
                    optionType="button"
                  >
                    <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                      {question.choices.map((choice: any, index: number) => (
                        <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </DesktopStyledRadioGroup>
                  {question.type !== 'radio-vertical' && (
                    <Sublabels>
                      <p>{question.option_label_left}</p>
                      <p>{question.option_label_right}</p>
                    </Sublabels>
                  )}
                  {subquestions && showSubquestionTitle && (
                    subquestions?.map((subq: any, index: number) => {
                      return (
                        <>
                          {(subq?.type === 'radio' || subq?.type === 'horizontal-radio' || subq?.type === 'radio-vertical') && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <DesktopStyledRadioGroup
                                onChange={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                value={subq?.choices.find((choice: any) => choice.selected)?.[0]}
                                optionType="button"
                              >
                                <Space direction={(subq.type === 'radio' || subq.type === 'radio-vertical') ? 'vertical' : 'horizontal'}>
                                  {subq.choices.map((choice: any, index: number) => (
                                    <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                                  ))}
                                </Space>
                              </DesktopStyledRadioGroup>
                              {subq?.type === 'horizontal-radio' && (
                                <Sublabels>
                                  <p>{question.option_label_left}</p>
                                  <p>{question.option_label_right}</p>
                                </Sublabels>
                              )}
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'textarea' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                          {subq?.type === 'input' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Input
                                defaultValue={subq.value}
                                onBlur={(e) => onChangeSubquestions(e.target.value, question.question_num, subq.type, index)}
                                placeholder={subq.placeholder ?? 'Write here...'}
                              />
                            </DesktopOptionsWrapper>
                          )}
                          {subq.type === 'multi-checkbox' && (
                            <DesktopOptionsWrapper key={question.question_num}>
                              <p>{subq.label}</p>
                              <Checkbox.Group
                                onChange={(checkedValues: any) => onChangeSubquestion(checkedValues, question.question_num, subq.type)}
                                defaultValue={subq.choices.filter((choice: any) => choice.selected).map((choice: any) => choice.title)}
                              >
                                <Space direction="vertical">
                                  {subq.choices.map((choice: any, index: number) => (
                                    <Checkbox key={`sub_${question.question_num}_${choice.title}_${index}`} value={choice.title}>{choice.title}</Checkbox>
                                  ))}
                                </Space>
                              </Checkbox.Group>
                            </DesktopOptionsWrapper>
                          )}
                        </>
                      )
                    })
                  )}
                  {subquestion && showSubquestionTitle && (
                    <>
                      {(subquestion?.type === 'input' || subquestion?.type === 'textarea') && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion?.label}</p>
                          <Input
                            defaultValue={subquestion?.value}
                            onBlur={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion?.type)}
                            placeholder={subquestion?.placeholder ?? 'Write here...'}
                          />
                        </DesktopOptionsWrapper>
                      )}
                      {(subquestion?.type === 'radio' || subquestion?.type === 'horizontal-radio' || subquestion?.type === 'radio-vertical') && (
                        <DesktopOptionsWrapper key={question.question_num}>
                          <p>{subquestion.label}</p>
                          <DesktopStyledRadioGroup
                            onChange={(e) => onChangeSubquestion(e.target.value, question.question_num, subquestion.type)}
                            value={subquestion?.choices.find((choice: any) => choice.selected)?.[0]}
                            optionType="button"
                          >
                            <Space direction={(subquestion.type === 'radio' || subquestion.type === 'radio-vertical') ? 'vertical' : 'horizontal'}>
                              {subquestion.choices.map((choice: any, index: number) => (
                                <Radio key={`sub_${question.question_num}_${choice.title}`} value={choice.title}>{choice.title}</Radio>
                              ))}
                            </Space>
                          </DesktopStyledRadioGroup>
                          {subquestion?.type === 'horizontal-radio' && (
                            <Sublabels>
                              <p>{question.option_label_left}</p>
                              <p>{question.option_label_right}</p>
                            </Sublabels>
                          )}
                        </DesktopOptionsWrapper>
                      )}
                    </>
                  )}
                </DesktopOptionsWrapper>
              )
            })
          }
        </DesktopQuestionWrapper>
      </DesktopStepContainer>
    </>
  )
};

const Step11 = () => {
  const { currentPage, questions, totalPage } = useSelector((state: RootState) => state.clientQuestionnaires.myIntegratedHealth);
  const dispatch = useDispatch();
  const onChangeRadio = (value: string, questionNumber: number) => {
    dispatch(clientQuestionnairesActions.toggleChoice({ value, questionNumber, type: 'myIntegratedHealth' }))
  };

  return (
    <>
      <StepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <QuestionsWrapper>
          <QuestionGroupTitle>K. Cellular Health Health</QuestionGroupTitle>
          <QuestionWrapper>
            <label>K1. Energy metabolism</label>
            {questions
              .filter((q) => q.question_type === 'K1. Energy metabolism')
              .map((question) => (
                <OptionsWrapper key={question.question_num}>
                  <p>{question.label}</p>
                  <StyledRadioGroup
                    onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                    value={question.choices.findIndex((choice: any) => choice.selected)}
                    optionType="button"
                  >
                    <Space direction="vertical">
                      {question.choices.map((choice: any, index: number) => (
                        <Radio value={index}>{choice.title}</Radio>
                      ))}
                    </Space>
                  </StyledRadioGroup>
                </OptionsWrapper>
              ))
            }
          </QuestionWrapper>
        </QuestionsWrapper>
      </StepContainer>
      <DesktopStepContainer>
        <h3>My Integrated Health</h3>
        <p>The aim of this questionnaire is to get a good picture of your health complaints. We use different categories for this. Would you like to try to answer all these questions one by one as best as possible?</p>
        <p>
          You can pause and continue answering the questions later. You save the data temporarily via the button “Continue later”.
        </p>
        <p>
          When you are done, you can save the answers via the “Submit” button. Your practitioner will then automatically receive the answers to the questions.
          Completing this list takes about 20 minutes."
        </p>
        <PageWrapper>
          <span>Page {currentPage} of {totalPage}</span>
        </PageWrapper>
        <DesktopStepTitle>
          K. Cellular Health Health
        </DesktopStepTitle>
        <DesktopQuestionWrapper>
          <label>K1. Energy metabolism</label>
          {questions
            .filter((q) => q.question_type === 'K1. Energy metabolism')
            .map((question) => (
              <DesktopOptionsWrapper key={question.question_num}>
                <p>{question.label}</p>
                <DesktopStyledRadioGroup
                  onChange={(e) => onChangeRadio(e.target.value, question.question_num)}
                  value={question.choices.find((choice: any) => choice.selected)?.title}
                  optionType="button"
                >
                  <Space direction={question.type === 'radio-vertical' ? 'vertical' : 'horizontal'}>
                    {question.choices.map((choice: any, index: number) => (
                      <Radio key={`${question.label}_${index}`} value={choice.title}>{choice.title}</Radio>
                    ))}
                  </Space>
                </DesktopStyledRadioGroup>
                {question.type !== 'radio-vertical' && (
                  <Sublabels>
                    <p>{question.option_label_left}</p>
                    <p>{question.option_label_right}</p>
                  </Sublabels>
                )}
              </DesktopOptionsWrapper>
            ))
          }
        </DesktopQuestionWrapper>
      </DesktopStepContainer>
    </>
  )
};

const steps = [
  {
    title: 'Attention',
    content: <Step1 />,
  },
  {
    title: 'Mood',
    content: <Step2 />,
  },
  {
    title: 'Personality',
    content: <Step3 />,
  },
  {
    title: 'Central Nervous System',
    content: <Step4 />,
  },
  {
    title: 'Peripheral Nervous System',
    content: <Step5 />,
  },
  {
    title: 'Locomotor System',
    content: <Step6 />,
  },
  {
    title: 'Neurohormonal System',
    content: <Step7 />,
  },
  {
    title: 'Cardiovascular System',
    content: <Step8 />,
  },
  {
    title: 'Resistance',
    content: <Step9 />,
  },
  {
    title: 'Digestion',
    content: <Step10 />,
  },
  {
    title: 'Cellular Health',
    content: <Step11 />,
  },
];

const desktopSteps = [
  {
    title: 'Attention',
    content: <Step1 />,
  },
  {
    title: 'Mood',
    content: <Step2 />,
  },
  {
    title: 'Personality',
    content: <Step3 />,
  },
  {
    title: 'Central Nervous System',
    content: <Step4 />,
  },
  {
    title: 'Peripheral Nervous System',
    content: <Step5 />,
  },
  {
    title: 'Locomotor System',
    content: <Step6 />,
  },
  {
    title: 'Neurohormonal System',
    content: <Step7 />,
  },
  {
    title: 'Cardiovascular System',
    content: <Step8 />,
  },
  {
    title: 'Resistance',
    content: <Step9 />,
  },
  {
    title: 'Digestion',
    content: <Step10 />,
  },
  {
    title: 'Cellular Health',
    content: <Step11 />,
  },
];

const MyIntegratedHealth = () => {
  const { questionnaireId } = useParams();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentPage, questions } = useSelector((state: RootState) => state.clientQuestionnaires.myIntegratedHealth);
  const { create: createQuestionnaireResponse, loading: loadingCreateQuestionnaireResponse } = useCreateQuestionnaireResponse();
  const [isNextEnabled, setIsNextEnabled] = useState(false);
  const items = steps.map((item) => ({ key: item.title, title: item.title }));

  const questionnaireTitle = useMemo(() => {
    if (user && 'questionnaireAssignments' in user) {
      const res = user?.questionnaireAssignments.filter((data: ClientQuestionnaireType) => data.id === questionnaireId)[0];
      if (res) {
        return res.questionnaire.title;
      }
    }
    return null;
  }, [user, questionnaireId]);

  const next = () => {
    setIsNextEnabled(false);
    dispatch(clientQuestionnairesActions.setCurrentPage({ currentPage: currentPage + 1, type: 'myIntegratedHealth' }));
  };

  const prev = () => {
    dispatch(clientQuestionnairesActions.setCurrentPage({ currentPage: currentPage - 1, type: 'myIntegratedHealth' }));
  };

  const onDone = async () => {
    const answers = questions.reduce((acc: Record<string, string>, question: any) => {
      const selectedChoice = question.choices.find((choice: any) => choice.selected);
      if (selectedChoice) {
        acc[`${question.group}-${question.order}`] = selectedChoice.title;
      }
      return acc;
    }, {});
    const result = await createQuestionnaireResponse({
      assignmentId: parseInt(questionnaireId ?? '0'),
      name: questionnaireTitle ?? 'My Integrated Health',
      answers: JSON.stringify(answers),
    });
    if (result.success) {
      message.success('Questionnaire response has been submitted successfully');
    }
  };

  const onChange = (value: number) => {
    dispatch(clientQuestionnairesActions.setCurrentPage({ currentPage: value + 1, type: 'myIntegratedHealth' }));
  };

  const checkAllQuestionsAnswered = useCallback(() => {
    const allAnswered = questions
      .filter((question) => question.page === currentPage)
      .every((question) => {
        const selectedChoice = question.choices.find((choice: any) => choice.selected);
        return selectedChoice !== undefined;
      });

    setIsNextEnabled(allAnswered);
  }, [questions, currentPage]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  useEffect(() => {
    checkAllQuestionsAnswered();
  }, [questions, checkAllQuestionsAnswered]);

  return (
    <>
      <MobileContainer>
        <MobileStyledSteps
          current={currentPage - 1}
          items={items}
          direction="horizontal"
          size="small"
          responsive={false}
        />
        <Content>{steps[currentPage - 1].content}</Content>
        <ButtonsContainer>
          {(currentPage - 1) > 0 && (
            <Button style={{ margin: '0 8px' }} onClick={prev}>
              Previous
            </Button>
          )}
          {(currentPage - 1) === steps.length - 1 && (
            <Button type="primary" onClick={onDone} loading={loadingCreateQuestionnaireResponse}>
              Done
            </Button>
          )}
          {(currentPage - 1) < steps.length - 1 && (
            <Button
              type="primary"
              onClick={next}
              icon={
                <MaterialIcon className="material-symbols-outlined" icon="trending_flat" />
              }
              iconPosition="end"
              disabled={!isNextEnabled}
            >
              Continue
            </Button>
          )}
        </ButtonsContainer>
      </MobileContainer>
      <DesktopContainer>
        <div>
          <StickyStepsContainer>
            <DesktopStyledSteps
              onChange={onChange}
              current={currentPage - 1}
              items={items}
              direction="vertical"
              size="small"
            />
          </StickyStepsContainer>
        </div>
        <div>
          <Content>{desktopSteps[currentPage - 1].content}</Content>
          <ButtonsContainer>
            {(currentPage - 1) > 0 && (
              <Button style={{ margin: '0 8px' }} onClick={prev}>
                Previous
              </Button>
            )}
            {(currentPage - 1) === steps.length - 1 && (
              <Button type="primary" onClick={onDone} loading={loadingCreateQuestionnaireResponse}>
                Done
              </Button>
            )}
            {(currentPage - 1) < steps.length - 1 && (
              <Button
                type="primary"
                onClick={next}
                icon={
                  <MaterialIcon className="material-symbols-outlined" icon="trending_flat" />
                }
                iconPosition="end"
                disabled={!isNextEnabled}
              >
                Next
              </Button>
            )}
          </ButtonsContainer>
        </div>
      </DesktopContainer>
    </>
  )
};

export default MyIntegratedHealth;
